/**
 * frontend/src/utils/preferences.ts
 * ===================================
 * Persistencia de preferencias del usuario en localStorage.
 *
 * Principio de independencia:
 *   Si en el futuro las preferencias se persisten en el backend, solo cambia
 *   este archivo.
 *
 * Las preferencias se guardan por usuario. Entre ellas están los destinatarios
 * del reporte: con una sola clave compartida, dos pilotos que usan la misma
 * máquina se heredan la lista y uno termina mandándole el reporte al otro.
 */

import type { Preferences } from "../types";
import { DEFAULT_PREFERENCES } from "../types";

const STORAGE_KEY = "conciliador_preferences";

/** Vacío en instalaciones sin usuarios: la clave queda como estaba. */
let usuarioActivo = "";

/** Define de quién son las preferencias que se leen y se guardan. */
export function usarPreferenciasDe(usuario: string): void {
  usuarioActivo = usuario;
}

function claveActual(): string {
  return usuarioActivo ? `${STORAGE_KEY}:${usuarioActivo}` : STORAGE_KEY;
}

export function loadPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(claveActual());
    if (!raw) return { ...DEFAULT_PREFERENCES };
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    // Merge with defaults so new fields added in future versions don't break
    return { ...DEFAULT_PREFERENCES, ...parsed };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function savePreferences(prefs: Preferences): void {
  try {
    localStorage.setItem(claveActual(), JSON.stringify(prefs));
  } catch {
    // localStorage unavailable (e.g., private mode) — fail silently
  }
}
